import React from 'react';
import WaveSurfer from 'wavesurfer.js';
import cx from 'classnames';
import { cssInJS } from '../../utils/styles';
import THEME from '../../shared/theme';

type Props = {
  className?: string;
  src: string;
  onPlay: (event: Event) => void;
  onPause: (event: Event) => void;
  onTimeUpdate: (event: Event) => void;
  onSeeked: (event: Event) => void;
  onSeeking: (event: Event) => void;
  onError: (event: Event) => void;
  seekTime?: number;
  initialTime?: number;
}

type State = {
  progress: number;
}

export default class Audio extends React.Component<Props, State> {
  audioNode: HTMLAudioElement | null;
  waveNode: HTMLDivElement | null;
  containerNode: HTMLDivElement | null;
  waveSurfer: WaveSurfer | null;
  _updatingWave: boolean;

  static styles: any;
  constructor(props: Props) {
    super(props);

    this.audioNode = null;
    this.waveNode = null;
    this.containerNode = null;
    this.waveSurfer = null;
    this._updatingWave = false;

    this.state = {
      progress: 0,
    };
  }

  componentDidMount() {
    this.audioNode?.addEventListener('play', this.handlePlay);
    this.audioNode?.addEventListener('pause', this.handlePause);
    this.audioNode?.addEventListener('timeupdate', this.handleTimeUpdate);
    this.audioNode?.addEventListener('seeked', this.handleSeeked);
    this.audioNode?.addEventListener('seeking', this.handleSeeking);
    this.audioNode?.addEventListener('error', this.props.onError);
    this.audioNode?.addEventListener('loadedmetadata', this.setInitialTime);

    this.waveSurfer = WaveSurfer.create({
      container: this.waveNode,
      waveColor: '#fff',
      progressColor: '#eb1300',
    });

    this.waveSurfer.setVolume(0);

    this.waveSurfer.load(this.props.src);

    this.waveSurfer.on('loading', this.handleWaveLoading);
    this.waveSurfer.on('ready', this.handleWaveReady);
    this.waveSurfer.on('seek', this.handleWaveSeek);
  }

  componentDidUpdate(prevProps) {
    if (this.props.src !== prevProps.src) {
      this.waveSurfer.load(this.props.src);
    }

    // let the parent set the current time after it's loaded
    if (this.props.seekTime !== prevProps.seekTime) {
      if (this.audioNode) {
        this.audioNode.currentTime = this.props.seekTime || prevProps.seekTime;
      }
      this.updateWave();
    }
  }

  componentWillUnmount() {
    // make sure it's unloaded
    this.waveSurfer?.destroy();
    this.audioNode?.removeEventListener('play', this.handlePlay);
    this.audioNode?.removeEventListener('pause', this.handlePause);
    this.audioNode?.removeEventListener('timeupdate', this.handleTimeUpdate);
    this.audioNode?.removeEventListener('seeked', this.handleSeeked);
    this.audioNode?.removeEventListener('seeking', this.handleSeeking);
    this.audioNode?.removeEventListener('error', this.props.onError);
    this.audioNode?.removeEventListener('loadedmetadata', this.setInitialTime);
    this.audioNode?.setAttribute('src', '');
    this.audioNode?.load();
  }

  render() {
    const { styles } = Audio;

    return (
      <div
        className={cx(styles.container, this.props.className)}
        ref={this.bindContainer}
      >
        <div
          className={styles.wave}
          ref={this.bindWave}
        />
        {this.renderProgress()}
        <audio
          className={styles.audio}
          key={this.props.src}
          preload="auto"
          controls
          src={this.props.src}
          ref={this.bindNode}
          playsInline
        />
      </div>
    );
  }

  renderProgress() {
    const { styles } = Audio;

    if (!this.isLoaded) {
      return (
        <div className={styles.progress}>
          Loading Audio...
          {' '}
          {this.state.progress}
          %
        </div>
      );
    }

    return null;
  }

  handlePlay = (event) => {
    this.waveSurfer.play();
    this.props.onPlay(event);
  };

  handlePause = (event) => {
    this.waveSurfer.pause();
    this.props.onPause(event);
  };

  handleTimeUpdate = (event) => {
    this.updateWave();
    this.props.onTimeUpdate(event);
  };

  handleSeeked = (event) => {
    this.updateWave();
    this.props.onSeeked(event);
  };

  handleSeeking = (event) => {
    this.updateWave();
    this.props.onSeeking(event);
  };

  handleWaveLoading = (progress) => {
    this.setState({ progress });
  };

  handleWaveReady = () => {
    this.setState({ progress: 100 });
  };

  handleWaveSeek = () => {
    if (this._updatingWave) {
      return;
    }
    if (this.audioNode) {
      this.audioNode.currentTime = this.waveSurfer.getCurrentTime();
    }
  };

  get isLoaded() {
    return this.state.progress === 100;
  }

  bindContainer = (node) => {
    this.containerNode = node;
  };

  bindWave = (node) => {
    this.waveNode = node;
  };

  bindNode = (node) => {
    this.audioNode = node;
  };

  updateWave() {
    // the flag breaks the infinite circular events since we're bidirectionally binding
    // the wave and the <audio> element. We don't want this action to cause another seek.
    this._updatingWave = true;

    this.waveSurfer?.seekTo(0);
    this.waveSurfer?.skip(this.audioNode?.currentTime);

    this._updatingWave = false;
  }

  setInitialTime = () => {
    if (this.audioNode) {
      this.audioNode.currentTime = this.props.initialTime || 0;
      this.updateWave();
    }
  };
}

const styles = cssInJS({
  container: {
    position: 'relative',
    width: '100%',
    display: 'inline-block',
    maxWidth: '85%',
    maxHeight: '85%',
    margin: 'auto auto',
    verticalAlign: 'middle',
    backgroundColor: '#000',
    boxShadow: '0 14px 45px rgba(0,0,0,0.25), 0 10px 18px rgba(0,0,0,0.22)',
    // @ts-ignore
    '@phone': {
      maxWidth: '100%',
      maxHeight: '100%',
      borderRadius: 0,
      boxShadow: 'none',
    },
  },

  progress: {
    textAlign: 'center',
    fontFamily: THEME.fontFamily,
    position: 'absolute',
    left: 0,
    right: 0,
    top: '40%',
    marginTop: -8,
  },

  audio: {
    width: '100%',
  },

  wave: {
  },
});

Audio.styles = styles;
